<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useDescriptor from '~/composables/useDescriptor'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import LearningPathLink from '~/components/utils/LearningPathLink.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = withDefaults(defineProps<{
  resource: BaseItem
  isAnchor?: boolean
  noImage?: boolean
  showLabel?: boolean
}>(), {
  showLabel: true
})

const { getTitle, isLearningPath } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()
const { t } = useI18n()

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const label = getDescriptor(props.resource)

const attributes = computed(() => isLearningPath(props.resource)
  ? {
      resource: props.resource
    }
  : {
      link: link.value,
      isAnchor: props.isAnchor
    }
)
</script>

<template>
  <div class="relative flex max-h-64 w-full animate-fade flex-col gap-2 bg-[--card-bg] px-6 py-4 text-xl font-bold text-[--card-text] outline outline-1 outline-[--card-border] ring ring-inset ring-[--card-ring] transition duration-100 has-[a:hover]:bg-[--card-hover] has-[a:hover]:ring-[--card-border]">
    <aside
      v-if="resource.new"
      class="pointer-events-none absolute right-0 top-0 z-20 rounded-bl-2xl bg-green-40 p-1.5 pl-2 text-sm font-medium uppercase tracking-wider text-white"
      v-text="t('newShort')"
    />
    <div
      :id="`card-${resource.locationId}`"
      class="contents"
    >
      <div
        v-if="showLabel"
        class="text-xs font-semibold uppercase text-[var(--card-accent)]"
      >
        {{ label }}
      </div>

      <div class="break-words text-lg font-medium">
        {{ title }}
      </div>
    </div>

    <img
      v-if="resource.thumbnail && !noImage"
      :src="resource.thumbnail"
      class="size-full overflow-hidden object-cover"
      alt=""
    >

    <component
      :is="isLearningPath(resource) ? LearningPathLink : LinkOrAnchor"
      :aria-labelledby="`card-${resource.locationId}`"
      class="absolute inset-0 ring-offset-1 focus-visible:ring"
      v-bind="attributes"
    />

    <div class="pointer-events-none relative mt-auto flex items-center gap-4">
      <CardMenu
        :resource="resource"
        button-variant="border"
        style="--ks-border: transparent"
        class="pointer-events-auto mr-auto"
      />
      <slot name="status" />
    </div>
  </div>
</template>
